import { defineComponent, PropType, ref, watch } from "vue";
import MemberMention from "../../../components/MemberMention.js";
import { useFetch, getFormattedDate, getFormattedTime } from "../../../utils.js";

type OverviewChannel = {
    id: string;
    name: string;
};

type VoiceState = {
    selfMute: boolean;
    selfDeaf: boolean;
    selfVideo: boolean;
    serverMute: boolean;
    serverDeaf: boolean;
    streaming: boolean;
    suppress: boolean;
};

type OverviewResponse = {
    available: boolean;
    membership: {
        present: boolean;
        everSeen: boolean;
        lastJoinAt: number | null;
        lastLeaveAt: number | null;
        gapBetweenLastJoinAndLeave: number | null;
    };
    voice: null | {
        channel: OverviewChannel | null;
        state: VoiceState;
        participants: {
            member: { id: string };
            state: VoiceState | null;
        }[] | { id: string }[];
    };
    channels: OverviewChannel[];
    lastActivity: {
        messageAt: number | null;
        voiceAt: number | null;
    };
    sanctions: {
        timeout: null | {
            executor: { id: string } | null;
            reason: string | null;
            expireAt: number;
            relatedLogCreatedAt: number;
        };
        mute: null | {
            executor: { id: string } | null;
            reason: string | null;
            expireAt: number;
            relatedLogCreatedAt: number;
        };
        ban: null | {
            executor: { id: string } | null;
            reason: string | null;
            relatedLogCreatedAt: number;
        };
    };
    recentMessages: {
        id: string;
        channel: string;
        content: string;
        createdAt: number;
    }[];
    recentVoiceSessions: {
        channelId: string;
        joinAt: number;
        leaveAt: number;
    }[];
};

const VoiceFlags = [
    [ "streaming", "screen_share", "Stream", "live" ],
    [ "selfVideo", "videocam", "Caméra", "live" ],
    [ "selfMute", "mic_off", "Micro coupé", "mute" ],
    [ "serverMute", "voice_over_off", "Mute serveur", "server" ],
    [ "selfDeaf", "headset_off", "Casque coupé", "mute" ],
    [ "serverDeaf", "hearing_disabled", "Sourd serveur", "server" ],
    [ "suppress", "volume_off", "Supprimé", "server" ],
] as const;

export default defineComponent({
    name: "UserOverviewMenu",
    props: {
        userId: {
            type: String,
            required: true,
        },
        user: {
            type: Object as PropType<{ id: string }>,
            required: true,
        },
    },
    setup(props) {
        const { fetch } = useFetch();
        const loading = ref(true);
        const overview = ref<OverviewResponse | null>(null);

        async function load() {
            loading.value = true;

            try {
                overview.value = await fetch(`/api/members/${props.userId}/overview`)
                    .then((res) => res.json());

            } finally {
                loading.value = false;
            }
        }

        watch(() => props.userId, load, { immediate: true });

        function getChannel(channelId: string | null | undefined) {
            if (!channelId)
                return null;

            return overview.value?.channels.find((channel) => channel.id === channelId) ?? null;
        }

        function renderChannel(channel: OverviewChannel | null) {
            if (!channel)
                return <span class="user-overview-muted">Salon inconnu</span>;

            return (
                <router-link to={`/channels/${channel.id}`} class="user-overview-channel">
                    #{channel.name}
                </router-link>
            );
        }

        function renderVoiceState(state: VoiceState) {
            const activeFlags = VoiceFlags.filter(([ key ]) => state[key]);

            if (!activeFlags.length)
                return <span class="user-overview-muted">Aucun statut vocal particulier.</span>;

            return (
                <div class="user-overview-badges">
                    {activeFlags.map(([ , icon, label, kind ]) => (
                        <span class={`user-overview-badge user-overview-badge-${kind}`}>
                            <span class="material-symbols-outlined">{icon}</span>
                            {label}
                        </span>
                    ))}
                </div>
            );
        }

        function renderVoiceStateIcons(state: VoiceState | null) {
            const activeFlags = state
                ? VoiceFlags.filter(([ key ]) => state[key])
                : [];

            if (!activeFlags.length)
                return null;

            return (
                <div class="user-overview-participant-flags">
                    {activeFlags.map(([ , icon, label, kind ]) => (
                        <span title={label} class={`material-symbols-outlined user-overview-voice-flag-${kind}`}>
                            {icon}
                        </span>
                    ))}
                </div>
            );
        }

        function renderCurrentVoice() {
            const voice = overview.value?.voice;

            if (!voice?.channel?.id)
                return (
                    <div class="user-overview-current-voice user-overview-current-voice-empty">
                        <div class="user-overview-current-header">
                            <span class="material-symbols-outlined">voice_over_off</span>
                            <div>
                                <b>Pas connecté en vocal</b>
                                <div class="user-overview-muted">Aucun salon vocal actif actuellement.</div>
                            </div>
                        </div>
                    </div>
                );

            const participants = voice.participants ?? [];

            return (
                <div class="user-overview-current-voice">
                    <div class="user-overview-current-header">
                        <span class="material-symbols-outlined">graphic_eq</span>
                        <div>
                            <b>Connecté en vocal</b>
                            <div>{renderChannel(voice.channel)}</div>
                        </div>
                    </div>

                    {renderVoiceState(voice.state)}

                    {participants.length > 0 && (
                        <>
                            <div class="user-overview-subtitle">Participants</div>
                            <div class="user-overview-participants-list">
                                {participants.map((participant) => {
                                    const participantMember = "member" in participant
                                        ? participant.member
                                        : participant;
                                    let participantState: VoiceState | null = null;

                                    if ("state" in participant)
                                        participantState = participant.state;
                                    else if (participantMember.id === props.userId)
                                        participantState = voice.state;

                                    if (!participantMember?.id)
                                        return null;

                                    return (
                                        <div class={{
                                            "user-overview-participant-row": true,
                                            "user-overview-participant-row-current": participantMember.id === props.userId,
                                        }}>
                                            <MemberMention id={participantMember.id} />
                                            <div class="user-overview-participant-right">
                                                {participantMember.id === props.userId && <span class="user-overview-you">Profil</span>}
                                                {renderVoiceStateIcons(participantState)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            );
        }

        function renderSanctionStatus() {
            const sanctions = overview.value?.sanctions;

            if (!sanctions?.timeout && !sanctions?.mute && !sanctions?.ban)
                return <div class="user-overview-empty">Aucune sanction active.</div>;

            return (
                <div class="user-overview-sanctions">
                    {sanctions.ban && (
                        <div class="user-overview-sanction user-overview-sanction-ban">
                            <span class="material-symbols-outlined">gavel</span>
                            <div>
                                <b>Banni</b>
                                <p>{sanctions.ban.reason || "Aucune raison renseignée."}</p>
                                {sanctions.ban.executor && (
                                    <small>Par <MemberMention id={sanctions.ban.executor.id} /></small>
                                )}
                            </div>
                        </div>
                    )}

                    {sanctions.mute && (
                        <div class="user-overview-sanction user-overview-sanction-timeout">
                            <span class="material-symbols-outlined">volume_off</span>
                            <div>
                                <b>Mute actif</b>
                                <p>{sanctions.mute.reason || "Aucune raison renseignée."}</p>
                                {sanctions.mute.executor && (
                                    <small>Par <MemberMention id={sanctions.mute.executor.id} /></small>
                                )}
                            </div>
                        </div>
                    )}

                    {sanctions.timeout && (
                        <div class="user-overview-sanction user-overview-sanction-timeout">
                            <span class="material-symbols-outlined">timer</span>
                            <div>
                                <b>Timeout actif</b>
                                <p>{sanctions.timeout.reason || "Aucune raison renseignée."}</p>
                                <small>
                                    Jusqu'au {sanctions.timeout.expireAt ? getFormattedDate(sanctions.timeout.expireAt) : "-"}
                                    {sanctions.timeout.executor && <> · Par <MemberMention id={sanctions.timeout.executor.id} /></>}
                                </small>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        function renderDateWithElapsed(timestamp: number | null) {
            if (!timestamp)
                return <span class="user-overview-muted">Inconnu</span>;

            return (
                <>
                    {getFormattedDate(timestamp)}
                    <small>Il y a {getFormattedLongDuration(Date.now() - timestamp)}</small>
                </>
            );
        }

        function getFormattedLongDuration(durationMs: number) {
            if (durationMs <= 48 * 60 * 60 * 1000)
                return getFormattedTime(durationMs);

            const hourMs = 60 * 60 * 1000;
            const dayMs = 24 * hourMs;
            const weekMs = 7 * dayMs;
            const monthMs = 30 * dayMs;
            let remaining = Math.max(0, durationMs);
            const months = Math.floor(remaining / monthMs);
            remaining -= months * monthMs;
            const weeks = Math.floor(remaining / weekMs);
            remaining -= weeks * weekMs;
            const days = Math.floor(remaining / dayMs);
            remaining -= days * dayMs;
            const hours = Math.floor(remaining / hourMs);
            const parts = [] as string[];

            if (months)
                parts.push(`${months} mois`);

            if (weeks)
                parts.push(`${weeks} sem`);

            if (days)
                parts.push(`${days}j`);

            parts.push(`${hours}h`);

            return parts.join(" ");
        }

        function renderMembershipStatus() {
            const membership = overview.value?.membership;

            if (!membership)
                return <div class="user-overview-empty">État serveur indisponible.</div>;

            if (!membership.everSeen)
                return (
                    <div class="user-overview-membership-empty">
                        <span class="material-symbols-outlined">person_off</span>
                        <div>
                            <b>Jamais vu sur ce serveur</b>
                            <small>Aucune présence ni log d'arrivée/départ n'a été trouvé.</small>
                        </div>
                    </div>
                );

            return (
                <div class="user-overview-membership">
                    <div class={{
                        "user-overview-membership-status": true,
                        "user-overview-membership-status-present": membership.present,
                        "user-overview-membership-status-absent": !membership.present,
                    }}>
                        <span class="material-symbols-outlined">{membership.present ? "how_to_reg" : "person_remove"}</span>
                        <div>
                            <b>{membership.present ? "Présent sur le serveur" : "Absent du serveur"}</b>
                            <small>
                                {membership.present
                                    ? "Le membre est actuellement dans la guilde."
                                    : "Le membre n'est actuellement pas dans la guilde."}
                            </small>
                        </div>
                    </div>

                    <div class="user-overview-membership-grid">
                        <div>
                            <span class="material-symbols-outlined">login</span>
                            <b>Dernière arrivée</b>
                            {renderDateWithElapsed(membership.lastJoinAt)}
                        </div>
                        <div>
                            <span class="material-symbols-outlined">logout</span>
                            <b>Dernier départ</b>
                            {renderDateWithElapsed(membership.lastLeaveAt)}
                        </div>
                    </div>

                    {membership.gapBetweenLastJoinAndLeave !== null && (
                        <div class="user-overview-membership-gap">
                            <span class="material-symbols-outlined">timer</span>
                            <span>Temps entre les deux : <b>{getFormattedLongDuration(membership.gapBetweenLastJoinAndLeave)}</b></span>
                        </div>
                    )}
                </div>
            );
        }

        return () => {
            if (loading.value)
                return <div class="notice">Chargement…</div>;

            if (!overview.value)
                return <div class="notice">Impossible de charger l'accueil du membre.</div>;

            return (
                <div class="user-overview">
                    <div class="user-overview-card">
                        <h2>État actuel</h2>
                        {renderCurrentVoice()}
                    </div>

                    <div class="user-overview-card">
                        <h2>Présence serveur</h2>
                        {renderMembershipStatus()}
                    </div>

                    <div class="user-overview-card">
                        <h2>Sanctions actives</h2>
                        {renderSanctionStatus()}
                    </div>

                    <div class="user-overview-card">
                        <h2>Dernière activité</h2>
                        <div class="user-overview-activity-grid">
                            <div>
                                <span class="material-symbols-outlined">chat</span>
                                <b>Message</b>
                                <small>{overview.value.lastActivity.messageAt ? getFormattedDate(overview.value.lastActivity.messageAt) : "Aucune activité"}</small>
                            </div>
                            <div>
                                <span class="material-symbols-outlined">settings_voice</span>
                                <b>Vocal</b>
                                <small>{overview.value.lastActivity.voiceAt ? getFormattedDate(overview.value.lastActivity.voiceAt) : "Aucune activité"}</small>
                            </div>
                        </div>
                    </div>

                    <div class="user-overview-card">
                        <h2>5 derniers messages</h2>
                        {overview.value.recentMessages.length ? (
                            <div class="user-overview-list">
                                {overview.value.recentMessages.map((message) => (
                                    <div class="user-overview-list-entry">
                                        <div>
                                            {renderChannel(getChannel(message.channel))}
                                            <small>{getFormattedDate(message.createdAt)}</small>
                                        </div>
                                        <p>{message.content || <span class="user-overview-muted">Message sans contenu texte</span>}</p>
                                    </div>
                                ))}
                            </div>
                        ) : <div class="user-overview-empty">Aucun message récent.</div>}
                    </div>

                    <div class="user-overview-card">
                        <h2>5 derniers vocaux rejoints</h2>
                        {overview.value.recentVoiceSessions.length ? (
                            <div class="user-overview-list">
                                {overview.value.recentVoiceSessions.map((session) => (
                                    <div class="user-overview-list-entry">
                                        <div>
                                            {renderChannel(getChannel(session.channelId))}
                                            <small>{getFormattedDate(session.joinAt)}</small>
                                        </div>
                                        <p>{getFormattedLongDuration(session.leaveAt - session.joinAt)}</p>
                                    </div>
                                ))}
                            </div>
                        ) : <div class="user-overview-empty">Aucune session vocale récente.</div>}
                    </div>
                </div>
            );
        };
    },
});
